import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Movie } from '@/mocks/movies'

interface HeroCarouselProps {
  slides: Movie[]
}

const categoryLabel: Record<Movie['category'], string> = {
  movie: '电影',
  tv: '电视剧',
  short: '短片',
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (paused || slides.length <= 1) return
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % slides.length)
    }, 6000)
    return () => window.clearInterval(timer)
  }, [paused, slides.length])

  if (slides.length === 0) return null

  const active = slides[index]

  const goTo = (next: number) => {
    setIndex((next + slides.length) % slides.length)
  }

  return (
    <section
      className="relative overflow-hidden rounded-xl bg-primary-950"
      aria-label="焦点影视推荐"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[480px] w-full md:h-[560px]">
        {slides.map((slide, slideIndex) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ease-portal ${
              slideIndex === index ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
            aria-hidden={slideIndex !== index}
          >
            <img
              src={slide.still}
              alt={`《${slide.title}》剧照`}
              title={`${slide.title} - 麻豆传媒在线焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/25" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/10 to-primary-950/40" />
          </div>
        ))}

        <div className="relative z-10 flex h-full flex-col justify-between p-5 md:p-8 lg:pr-[300px]">
          <div className="flex items-start justify-between gap-3">
            <span className="flex items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1 text-[11px] tracking-wide text-background-100 backdrop-blur-sm">
              <i className="ri-flashlight-fill text-accent-400" />
              本周焦点 · 精选首播
            </span>
            <Link
              to="/about"
              className="hidden items-center gap-1.5 whitespace-nowrap rounded-full border border-background-50/20 px-3 py-1 text-[11px] text-background-200 transition-colors hover:border-accent-400 hover:text-accent-300 sm:flex"
            >
              <i className="ri-information-line" />
              站点说明
            </Link>
          </div>

          <div key={active.id} className="animate-fade-up max-w-xl">
            <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] text-background-200">
              <span className="rounded bg-accent-500 px-2 py-0.5 font-medium text-background-50">
                {categoryLabel[active.category]}
              </span>
              <span className="font-label tracking-widest">{active.year}</span>
              <span className="h-3 w-px bg-background-50/30" />
              <span>{active.duration}</span>
              <span className="h-3 w-px bg-background-50/30" />
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-[10px] text-accent-400" />
                {active.rating.toFixed(1)}
              </span>
            </div>

            <h2 className="text-3xl font-bold leading-tight tracking-tight text-background-50 md:text-4xl">
              {active.title}
            </h2>
            <p className="mt-2 text-sm text-accent-200 md:text-base">{active.tagline}</p>
            <p className="mt-3 hidden max-w-lg text-sm leading-6 text-background-300 md:line-clamp-2">
              {active.synopsis}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-play-fill text-base" />
                立即观看
              </Link>
              <Link
                to={`/detail/${active.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-5 py-2.5 text-sm text-background-100 transition-colors hover:border-background-50/70 hover:bg-background-50/10"
              >
                查看详情
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {slides.map((slide, slideIndex) => (
                <button
                  key={slide.id}
                  type="button"
                  aria-label={`切换到《${slide.title}》`}
                  onClick={() => goTo(slideIndex)}
                  className={`h-1 cursor-pointer rounded-full transition-all duration-300 ${
                    slideIndex === index
                      ? 'w-8 bg-accent-500'
                      : 'w-4 bg-background-50/35 hover:bg-background-50/60'
                  }`}
                />
              ))}
            </div>

            <div className="flex items-center gap-3">
              <span className="font-label text-xs tracking-[0.2em] text-background-200">
                {String(index + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => goTo(index - 1)}
                  aria-label="上一部"
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                <button
                  type="button"
                  onClick={() => goTo(index + 1)}
                  aria-label="下一部"
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute right-5 top-1/2 z-20 hidden w-[240px] -translate-y-1/2 flex-col gap-2 lg:flex">
          {slides.map((slide, slideIndex) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => goTo(slideIndex)}
              className={`flex cursor-pointer items-center gap-2.5 rounded-md border p-2 text-left transition-colors ${
                slideIndex === index
                  ? 'border-accent-500/80 bg-primary-950/70'
                  : 'border-background-50/15 bg-primary-950/35 hover:border-background-50/35'
              }`}
            >
              <span className="h-11 w-14 shrink-0 overflow-hidden rounded-sm bg-primary-900">
                <img
                  src={slide.still}
                  alt={`${slide.title}缩略图`}
                  className="h-full w-full object-cover object-top"
                />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-xs font-medium text-background-50">
                  {slide.title}
                </span>
                <span className="mt-0.5 block truncate text-[10px] text-background-300">
                  {categoryLabel[slide.category]} · {slide.year}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}